import { partition, sortBy } from 'lodash-es'

import type { IconProps } from '@fv/client-components'
import type { FullShipment, Quote } from '@fv/client-types'

import { useEnabledCarriers } from '@/hooks/useEnabledCarriers'
import { isProcessingQuotes } from '@/utils/shipmentFuncs'

import { useRole } from '../auth/hooks'
import { quoteFuncs } from './quoteFuncs'

export type RatesListingMode = 'view' | 'change' | undefined
export type SortType = 'cheapest' | 'fastest'

export const rateSortTypes: Array<{ type: SortType; icon: IconProps['icon'] }> =
  [
    {
      type: 'cheapest',
      icon: 'dollar-sign',
    },
    {
      type: 'fastest',
      icon: 'truck-fast',
    },
  ]
export interface RateListingData {
  manualRates: Quote[]
  activeApiRates: Quote[]
  erroredRates: Quote[]
  pendingRates: Quote[]
}
export function useRateListing(
  {
    workflow,
    source,
    quoteProcessStatus,
  }: Pick<FullShipment, 'source' | 'workflow' | 'quoteProcessStatus'>,
  rates: Quote[] = [],
  sortType: SortType,
): RateListingData {
  const apiCarriers = useEnabledCarriers(workflow)
  const allRates = rates.slice()
  const role = useRole()

  allRates.push(
    ...apiCarriers
      .filter(
        c =>
          // Add processing/errored rate for API carriers with no response
          !allRates.some(
            r => r.providerCode === c.code || r.assetCarrierCode === c.code,
          ) && role === 'shipper',
      )
      .map<Quote>(c => {
        const carrierName = c.label.trim()
        const isProcessing = isProcessingQuotes({ quoteProcessStatus })
        let message = `${carrierName} did not return rates.`

        if (source === 'v1') {
          message += `  Please ensure you are not excluding this carrier when rating via an integration.`
        }
        return {
          _id: c._id,
          amount: 0,
          assetCarrierCode: c.code,
          assetCarrierName: carrierName,
          carrierId: c._id,
          createdDate: new Date().toISOString(),
          carrierSort: carrierName.toLowerCase(),
          currency: 'usd',
          equipmentType: workflow === 'parcel' ? 'parcel' : 'ltl',
          method: 'api',
          mode: workflow === 'parcel' ? 'parcel' : 'ltl',
          pricingMethod: 'capacity',
          pricingType: 'pallet',
          providerCode: c.code,
          providerName: carrierName,
          quoteRequestId: '',
          serviceId: '',
          source: 'carrier',
          status: isProcessing ? 'active' : 'error',
          ...(!isProcessing && {
            errors: [
              {
                message,
                type: 'external',
              },
            ],
          }),
        }
      }),
  )

  const [active, errored] = partition(allRates, r => r.status !== 'error')
  const sortedRates = sortRates(sortType, active)
  const [manualRates, apiRates] = partition(sortedRates, r => {
    return r.method === 'manual' && !quoteFuncs.isSpotWorkflow(r)
  })

  // List manual rates first
  const activeApiRates = apiRates.filter(
    r => !quoteFuncs.isPendingQuote(r, { quoteProcessStatus }),
  )
  const pendingApiRates = apiRates.filter(
    r =>
      quoteFuncs.isPendingQuote(r, { quoteProcessStatus }) &&
      !activeApiRates.some(
        a =>
          getCarrierIdentifier(a) === getCarrierIdentifier(r) ||
          a.providerCode === r.providerCode,
      ),
  )

  return {
    activeApiRates,
    erroredRates: errored,
    pendingRates: pendingApiRates,
    manualRates,
  }
}

export function getCarrierIdentifier(
  quote: Quote,
  groupBy: 'carrier' | 'broker' = 'carrier',
) {
  // When grouping by broker, use the provider (broker) identifier
  if (groupBy === 'broker') {
    const code = quote.providerCode
    if (code) {
      return code
    }
    return quote.providerName
  }

  // When grouping by carrier (default), use the asset carrier identifier
  const code = quote.assetCarrierCode ?? quote.providerCode
  if (code) {
    return code
  }
  return quote.providerName
}

const sortRates = (type: SortType, quotes: Quote[]): Quote[] => {
  if (type === 'cheapest') {
    return sortBy(
      quotes,
      q => q.status !== 'awarded',
      'amount',
      'transitDaysMin',
      q => quoteFuncs.guaranteedSort(q),
      'deliveryDate',
      'serviceId',
      q => quoteFuncs.carrierSort(q),
    )
  } else {
    return sortBy(
      quotes,
      q => q.status !== 'awarded',
      q => quoteFuncs.interlineSort(q),
      'transitDaysMin',
      'deliveryDate',
      q => quoteFuncs.guaranteedSort(q),
      'amount',
      'serviceId',
      q => quoteFuncs.carrierSort(q),
    )
  }
}
